import React, { useState, useMemo, useCallback, useRef } from "react";
import { Select, Divider, Button, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import debounce from "lodash/debounce";
import { axiosInstance } from "../../providers/dataProvider";
import { resolveItemLabel } from "../../utils/displayUtils";

export interface ItemSelectOption {
  label: string;
  value: string;
  description?: string;
}

interface ItemReferenceSelectProps {
  /** Pre-loaded options from useRelatedItems */
  options?: ItemSelectOption[];
  /** The Aras ItemType name to search (e.g., "Class") */
  dataSource?: string;
  disabled?: boolean;
  placeholder?: string;
  notFoundContent?: string;
  /** Current value */
  value?: string;
  onChange?: (value: string | undefined) => void;
  /** Show "Add new" button */
  showAddNew?: boolean;
  addNewLabel?: string;
  onOpenAddNewModal?: () => void;
}

/**
 * Select component for item reference fields with server-side search.
 * Pre-loaded options are shown initially; typing triggers an API search
 * that can find items beyond the initial page.
 */
const ItemReferenceSelect: React.FC<ItemReferenceSelectProps> = ({
  options: preloadedOptions = [],
  dataSource,
  disabled,
  placeholder,
  notFoundContent,
  value,
  onChange,
  showAddNew,
  addNewLabel,
  onOpenAddNewModal,
}) => {
  const [searchResults, setSearchResults] = useState<ItemSelectOption[]>([]);
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const fetchRef = useRef(0);

  // Debounced server search
  const handleSearch = useMemo(
    () =>
      debounce(async (searchText: string) => {
        if (!searchText || !dataSource) {
          setSearchResults([]);
          setHasSearched(false);
          return;
        }

        fetchRef.current += 1;
        const fetchId = fetchRef.current;
        setSearching(true);

        try {
          const { data } = await axiosInstance.get(
            `items/${dataSource}`,
            { params: { search: searchText, pageSize: 20 } }
          );

          // Only update if this is still the latest request
          if (fetchId !== fetchRef.current) return;

          const items = (data?.data || []) as Record<string, unknown>[];
          const results: ItemSelectOption[] = items.map((item) => ({
            label: resolveItemLabel(item),
            value: (item.id as string) || "",
            description: buildDescription(item),
          }));

          setSearchResults(results);
          setHasSearched(true);
        } catch {
          // Silently fail - user still has pre-loaded options
          setSearchResults([]);
        } finally {
          if (fetchId === fetchRef.current) {
            setSearching(false);
          }
        }
      }, 300),
    [dataSource]
  );

  // Merge pre-loaded options with search results, deduplicating by value
  const mergedOptions = useMemo(() => {
    if (!hasSearched) return preloadedOptions;

    // When searching, show search results first, then pre-loaded items not in results
    const resultIds = new Set(searchResults.map((r) => r.value));
    const remaining = preloadedOptions.filter((o) => !resultIds.has(o.value));
    return [...searchResults, ...remaining];
  }, [preloadedOptions, searchResults, hasSearched]);

  // When search is cleared, reset to pre-loaded options
  const handleSearchChange = useCallback(
    (searchText: string) => {
      if (!searchText) {
        setSearchResults([]);
        setHasSearched(false);
        handleSearch.cancel();
      } else {
        handleSearch(searchText);
      }
    },
    [handleSearch]
  );

  return (
    <Select
      value={value}
      onChange={onChange}
      disabled={disabled}
      placeholder={placeholder}
      allowClear
      showSearch
      filterOption={
        hasSearched
          ? false // Server already filtered
          : (input, option) => {
              const searchText = (
                (option?.label?.toString() ?? "") +
                " " +
                ((option as Record<string, unknown>)?.description ?? "")
              ).toLowerCase();
              return searchText.includes(input.toLowerCase());
            }
      }
      onSearch={handleSearchChange}
      options={mergedOptions.map((opt) => ({
        label: opt.label,
        value: opt.value,
        description: opt.description || "",
      }))}
      optionRender={(option) => {
        const desc = (option.data as Record<string, unknown>)
          ?.description as string;
        return (
          <div>
            <div>{option.label}</div>
            {desc && (
              <div
                style={{
                  fontSize: 12,
                  color: "var(--ant-color-text-secondary)",
                  lineHeight: "18px",
                }}
              >
                {desc}
              </div>
            )}
          </div>
        );
      }}
      notFoundContent={
        searching ? <Spin size="small" /> : notFoundContent
      }
      dropdownRender={
        showAddNew && onOpenAddNewModal
          ? (menu) => (
              <>
                {menu}
                <Divider style={{ margin: "8px 0" }} />
                <Button
                  type="text"
                  icon={<PlusOutlined />}
                  onClick={onOpenAddNewModal}
                  style={{ width: "100%", textAlign: "left" }}
                >
                  {addNewLabel}
                </Button>
              </>
            )
          : undefined
      }
    />
  );
};

/**
 * Build a short description from item fields (similar to useRelatedItems logic).
 */
function buildDescription(item: Record<string, unknown>): string {
  const skipKeys = new Set([
    "id",
    "name",
    "keyedName",
    "keyed_name",
    "itemNumber",
    "item_number",
  ]);

  const parts: string[] = [];
  const usedValues = new Set<string>();

  // Add keyed_name and item_number as context
  const keyedName = (item.keyedName as string) || (item.keyed_name as string);
  const itemNumber =
    (item.itemNumber as string) || (item.item_number as string);
  const name = item.name as string;

  if (keyedName && keyedName !== name) {
    parts.push(keyedName);
    usedValues.add(keyedName);
  }
  if (itemNumber && !usedValues.has(itemNumber)) {
    parts.push(itemNumber);
    usedValues.add(itemNumber);
  }

  // Add a few more meaningful fields
  for (const [key, rawVal] of Object.entries(item)) {
    if (parts.length >= 3) break;
    if (skipKeys.has(key)) continue;
    if (typeof rawVal !== "string") continue;
    const val = rawVal.trim();
    if (
      val &&
      val.length <= 80 &&
      !usedValues.has(val) &&
      !isInternalValue(val) &&
      !isSystemKey(key)
    ) {
      parts.push(val);
      usedValues.add(val);
    }
  }

  return parts.join(" · ");
}

function isSystemKey(key: string): boolean {
  const lower = key.toLowerCase();
  return (
    lower.endsWith("id") ||
    lower.endsWith("display") ||
    lower === "currentstate" ||
    (lower.endsWith("on") &&
      (lower.startsWith("created") || lower.startsWith("modified")))
  );
}

function isInternalValue(val: string): boolean {
  if (/^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(val)) return true;
  if (/^[0-9a-f]{32}$/i.test(val)) return true;
  if (/^Identity_/i.test(val)) return true;
  if (/^\d{4}-\d{2}-\d{2}T/.test(val)) return true;
  return false;
}

export default ItemReferenceSelect;
